import React, { useState } from 'react';
import { format, isToday, isTomorrow, isPast, parseISO, differenceInDays } from 'date-fns';
import { Calendar, Check, Play, Clock, Trash2, MessageSquare, Video, Link as LinkIcon } from 'lucide-react';
import { CATEGORIES, COLORS } from '@/data';
import { getRedGifsThumbnail } from '@/lib/utils';
import { EmptyState } from '@/components/ui';
import type { ScheduledActivity, Clip } from '@/types';
import { RedGifsImage } from '@/components/RedGifsImage';

interface UpcomingActivitiesProps {
  scheduledActivities: ScheduledActivity[];
  clips: Clip[];
  onViewClip: (clip: Clip) => void;
  onMarkComplete: (activityId: string, note?: string, userClipUrl?: string) => void;
  onReschedule: (activityId: string) => void;
  onDelete: (activityId: string) => void;
  limit?: number;
  showCompleted?: boolean;
}

export const UpcomingActivities: React.FC<UpcomingActivitiesProps> = ({
  scheduledActivities,
  clips,
  onViewClip,
  onMarkComplete,
  onReschedule,
  onDelete,
  limit = 5,
  showCompleted = false,
}) => {
  const [completingActivityId, setCompletingActivityId] = useState<string | null>(null);
  const [completionNote, setCompletionNote] = useState('');
  const [userClipUrl, setUserClipUrl] = useState('');

  // Filter and sort upcoming activities
  const upcomingActivities = scheduledActivities
    .filter(activity => !activity.completedAt)
    .sort((a, b) => new Date(a.scheduledDate).getTime() - new Date(b.scheduledDate).getTime())
    .slice(0, limit);

  // Get recently completed activities
  const completedActivities = showCompleted
    ? scheduledActivities
        .filter(activity => activity.completedAt)
        .sort((a, b) => new Date(b.completedAt!).getTime() - new Date(a.completedAt!).getTime())
        .slice(0, 3)
    : [];

  const getDateLabel = (dateStr: string) => {
    const date = parseISO(dateStr);
    if (isToday(date)) return 'Today';
    if (isTomorrow(date)) return 'Tomorrow';

    const daysUntil = differenceInDays(date, new Date());
    if (daysUntil < 7 && daysUntil > 0) {
      return format(date, 'EEEE'); // Day name
    }
    return format(date, 'MMM d'); // "Jan 15"
  };

  const isOverdue = (dateStr: string) => {
    const date = parseISO(dateStr);
    return isPast(date) && !isToday(date);
  };

  const handleDoneClick = (activityId: string) => {
    setCompletingActivityId(activityId);
    setCompletionNote('');
    setUserClipUrl('');
  };

  const handleConfirmComplete = () => {
    if (completingActivityId) {
      onMarkComplete(completingActivityId, completionNote.trim() || undefined, userClipUrl.trim() || undefined);
      setCompletingActivityId(null);
      setCompletionNote('');
      setUserClipUrl('');
    }
  };

  const handleCancelComplete = () => {
    setCompletingActivityId(null);
    setCompletionNote('');
    setUserClipUrl('');
  };

  if (upcomingActivities.length === 0 && completedActivities.length === 0) {
    return (
      <div className="bg-zinc-800/50 rounded-xl border border-zinc-700/50 p-4">
        <EmptyState
          icon={<Calendar className="w-6 h-6" style={{ color: COLORS.lime }} />}
          title="No activities scheduled"
          description="Plan your next date night!"
        />
      </div>
    );
  }

  const renderActivity = (activity: ScheduledActivity, isCompleted: boolean = false) => {
    const clip = clips.find(c => c.id === activity.clipId);
    const category = clip ? CATEGORIES.find(c => c.id === clip.categoryId) : null;
    const overdue = !isCompleted && isOverdue(activity.scheduledDate);
    const thumbnail = clip ? getRedGifsThumbnail(clip.link) : null;
    const isBeingCompleted = completingActivityId === activity.id;

    if (!clip) return null;

    return (
      <div key={activity.id} className={`p-4 ${isCompleted ? 'opacity-60' : ''}`}>
        {isBeingCompleted ? (
          // Completion form
          <div className="bg-zinc-900 rounded-xl p-4 border border-lime-500/50">
            <div className="flex items-center gap-2 mb-3">
              <Check className="w-5 h-5 text-lime-400" />
              <span className="text-white font-medium">Mark as Done</span>
            </div>
            <p className="text-zinc-400 text-sm mb-3">
              {category?.emoji} {clip.title}
            </p>
            <div className="mb-3">
              <label className="block text-xs text-zinc-500 mb-1">
                How was it? (optional note for your history)
              </label>
              <textarea
                value={completionNote}
                onChange={e => setCompletionNote(e.target.value)}
                placeholder="Amazing! We loved this one..."
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white placeholder-zinc-500 text-sm resize-none h-16"
                autoFocus
              />
            </div>
            {/* User Clip Submission */}
            <div className="mb-3">
              <label className="block text-xs text-zinc-500 mb-1 flex items-center gap-1">
                <Video className="w-3 h-3" /> Share your own clip! (optional)
              </label>
              <div className="flex items-center gap-2">
                <LinkIcon className="w-4 h-4 text-zinc-500" />
                <input
                  type="url"
                  value={userClipUrl}
                  onChange={e => setUserClipUrl(e.target.value)}
                  placeholder="https://redgifs.com/watch/..."
                  className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white placeholder-zinc-500 text-sm"
                />
              </div>
              <p className="text-xs text-zinc-600 mt-1">If you made your own video of this activity, share the link!</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleConfirmComplete}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-zinc-900"
                style={{ backgroundColor: COLORS.lime }}
              >
                <Check className="w-4 h-4" /> Complete
              </button>
              <button
                onClick={handleCancelComplete}
                className="px-3 py-2 rounded-lg text-sm text-zinc-400 hover:text-white bg-zinc-800"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          // Normal activity display
          <div className="flex gap-3">
            {/* Thumbnail */}
            <div className="w-14 h-14 rounded-lg overflow-hidden bg-zinc-900 flex-shrink-0 relative">
              {thumbnail ? (
                <RedGifsImage
                  url={clip.link}
                  alt={clip.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Play className="w-5 h-5" style={{ color: COLORS.lime }} />
                </div>
              )}
              {isCompleted && (
                <div className="absolute inset-0 bg-green-500/30 flex items-center justify-center">
                  <Check className="w-6 h-6 text-green-400" />
                </div>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-white font-medium text-sm truncate">
                    {category?.emoji} {clip.title}
                  </p>
                  <p className={`text-xs mt-0.5 ${overdue ? 'text-red-400' : isCompleted ? 'text-green-400' : 'text-zinc-500'}`}>
                    {overdue && '⚠️ '}
                    {isCompleted ? `Completed ${format(parseISO(activity.completedAt!), 'MMM d')}` : getDateLabel(activity.scheduledDate)}
                  </p>
                  {/* Show completion note if exists */}
                  {activity.notes && (
                    <p className="text-xs text-zinc-400 mt-1 flex items-center gap-1">
                      <MessageSquare className="w-3 h-3" /> {activity.notes}
                    </p>
                  )}
                  {/* Show who scheduled */}
                  {activity.createdBy?.name && (
                    <p className="text-xs text-zinc-500 mt-1">
                      📅 Scheduled by {activity.createdBy.name}
                    </p>
                  )}
                  {/* Show user submitted clip URL if exists */}
                  {activity.userClipUrl && (
                    <a
                      href={activity.userClipUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-pink-400 hover:text-pink-300 mt-1 flex items-center gap-1 truncate"
                      onClick={e => e.stopPropagation()}
                    >
                      <Video className="w-3 h-3 flex-shrink-0" /> Their clip
                    </a>
                  )}
                </div>
              </div>

              {/* Actions */}
              {!isCompleted && (
                <div className="flex flex-wrap gap-2 mt-2">
                  <button
                    onClick={() => onViewClip(clip)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-700 hover:bg-zinc-600 active:bg-zinc-500 rounded-lg text-xs text-zinc-300 min-h-[32px]"
                  >
                    <Play className="w-3.5 h-3.5" /> Watch
                  </button>
                  <button
                    onClick={() => handleDoneClick(activity.id)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-zinc-900 min-h-[32px] active:opacity-80"
                    style={{ backgroundColor: COLORS.lime }}
                  >
                    <Check className="w-3.5 h-3.5" /> Done
                  </button>
                  <button
                    onClick={() => onReschedule(activity.id)}
                    className="flex items-center justify-center px-2.5 py-1.5 bg-zinc-700 hover:bg-zinc-600 active:bg-zinc-500 rounded-lg text-xs text-zinc-300 min-h-[32px] min-w-[32px]"
                  >
                    <Calendar className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => onDelete(activity.id)}
                    className="flex items-center justify-center px-2.5 py-1.5 bg-zinc-700 hover:bg-zinc-600 active:bg-zinc-500 rounded-lg text-xs text-red-400 min-h-[32px] min-w-[32px]"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Upcoming Activities */}
      {upcomingActivities.length > 0 && (
        <div className="bg-zinc-800/50 rounded-xl border border-zinc-700/50">
          <div className="flex items-center justify-between p-4 border-b border-zinc-700/50">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Clock className="w-4 h-4" style={{ color: COLORS.lime }} />
              Upcoming
            </h3>
            <span className="text-zinc-500 text-sm">{upcomingActivities.length} planned</span>
          </div>

          <div className="divide-y divide-zinc-700/50">
            {upcomingActivities.map(activity => renderActivity(activity, false))}
          </div>
        </div>
      )}

      {/* Recently Completed */}
      {completedActivities.length > 0 && (
        <div className="bg-zinc-800/30 rounded-xl border border-zinc-700/30">
          <div className="flex items-center justify-between p-4 border-b border-zinc-700/30">
            <h3 className="text-zinc-400 font-semibold flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400" />
              Recently Done
            </h3>
          </div>

          <div className="divide-y divide-zinc-700/30">
            {completedActivities.map(activity => renderActivity(activity, true))}
          </div>
        </div>
      )}
    </div>
  );
};
